import { use, useEffect, useState } from 'react';
import { useForm, UseFormReturn } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import ConfirmationPopupProviderContext from 'shared/components/confirmation-pop-up/providers/contexts/confirmation-popup-provider-context';
import ErrorMessageProviderContext from 'shared/components/error/providers/contexts/error-message-context';
import ViewingMode from 'shared/components/viewing-mode-controller/types/viewing-mode';

import { EmergencyAlarmLocationType } from 'features/emergency-alarm-settings/domain/models/emergency-alarm-location-type';
import {
  useDeleteEmergencyAlarmSetting,
  useReadEmergencyAlarmSettings,
  useUpsertEmergencyAlarmSetting,
} from '../context/emergency-alarm-settings-queries';
import { EmergencyAlarmActivationMethod } from '../domain/models/emergency-alarm-activation-method';
import { EmergencyAlarmDeviceTriggerAction } from '../domain/models/emergency-alarm-device-trigger-action';
import EmergencyAlarmSetting, { preAlarmDurationOptions } from '../domain/models/emergency-alarm-setting';

export type EmergencyAlarmSettingsFormValues = {
  emergencyAlarmEnabled: boolean;
  divergentSettingEnabled: boolean;
  activationMethod: EmergencyAlarmActivationMethod;
  deviceTriggerAction: EmergencyAlarmDeviceTriggerAction;
  preAlarmDurationS: number;
  preAlarmEnabled: boolean;
  soundEnabled: boolean;
  locationType: EmergencyAlarmLocationType;
};

export type EmergencyAlarmSettingsDetailsHook = {
  currentSelectedEmergencyAlarmSettings?: EmergencyAlarmSetting;
  viewingMode: ViewingMode;
  form: UseFormReturn<EmergencyAlarmSettingsFormValues>;
  upsertIsLoading: boolean;
  deleteIsLoading: boolean;
  selectSettingDetails(setting?: EmergencyAlarmSetting): void;
  submitEmergencyAlarmSetting: (fieldValues: EmergencyAlarmSettingsFormValues) => void;
  cancelEditing(): void;
  switchToEditingMode(): void;
};

const useEmergencyAlarmSettingsDetails = (): EmergencyAlarmSettingsDetailsHook => {
  const { t } = useTranslation('emergencyAlarm');
  const { showConfirmationPopUp, showDefaultConfirmationPopUp, enablePopUpGuard } = use(ConfirmationPopupProviderContext);

  const { setErrorMessage } = use(ErrorMessageProviderContext);
  const [viewingMode, setViewingMode] = useState<ViewingMode>('none');
  const [currentSelectedEmergencyAlarmSettings, setCurrentSelectedEmergencyAlarmSettings] = useState<EmergencyAlarmSetting>();

  const { data: emergencyAlarmSettingsResponse } = useReadEmergencyAlarmSettings();

  const form = useForm<EmergencyAlarmSettingsFormValues>({
    mode: 'onBlur',
    defaultValues: {
      emergencyAlarmEnabled: false,
      divergentSettingEnabled: false,
      activationMethod: EmergencyAlarmActivationMethod.DeviceButton,
      deviceTriggerAction: EmergencyAlarmDeviceTriggerAction.TwoPresses,
      preAlarmDurationS: preAlarmDurationOptions[2],
      preAlarmEnabled: true,
      soundEnabled: true,
      locationType: EmergencyAlarmLocationType.None,
    },
  });

  const watchDivergentSettingEnabled = form.watch('divergentSettingEnabled');

  const {
    mutate: upsertEmergencyAlarmSetting,
    isPending: upsertEmergencyAlarmSettingIsLoading,
    isSuccess: upsertEmergencyAlarmSettingIsSuccess,
    isError: upsertEmergencyAlarmSettingIsError,
    error: upsertEmergencyAlarmSettingError,
    data: upsertEmergencyAlarmSettingData,
  } = useUpsertEmergencyAlarmSetting();

  const updateCurrentEmergencyAlarmSetting = (setting: EmergencyAlarmSetting): void => {
    const updatedSetting: EmergencyAlarmSetting = {
      ...setting,
      name: currentSelectedEmergencyAlarmSettings?.name,
    };

    setCurrentSelectedEmergencyAlarmSettings(updatedSetting);

    form.reset({
      ...setting,
      divergentSettingEnabled: !setting.isDefaultTemplateSetting,
    });
  };

  const {
    mutate: deleteEmergencyAlarmSetting,
    isPending: deleteEmergencyAlarmSettingIsLoading,
    isError: deleteEmergencyAlarmSettingIsError,
    error: deleteEmergencyAlarmSettingError,
    data: deleteEmergencyAlarmSettingData,
  } = useDeleteEmergencyAlarmSetting();

  const resetCurrentEmergencyAlarmSettingToDefault = (): void => {
    if (emergencyAlarmSettingsResponse?.defaultTemplateSetting && currentSelectedEmergencyAlarmSettings) {
      const revertedSetting: EmergencyAlarmSetting = {
        ...emergencyAlarmSettingsResponse?.defaultTemplateSetting,
        organisationUnitId: currentSelectedEmergencyAlarmSettings?.organisationUnitId,
        name: currentSelectedEmergencyAlarmSettings?.name,
        isDefaultTemplateSetting: true,
      };

      setCurrentSelectedEmergencyAlarmSettings(revertedSetting);
    }

    form.reset({
      ...emergencyAlarmSettingsResponse?.defaultTemplateSetting,
      divergentSettingEnabled: false,
    });
  };

  const selectSettingDetails = (setting?: EmergencyAlarmSetting): void => {
    if (!setting) {
      return;
    }

    const hasOrganisationSetting = emergencyAlarmSettingsResponse && emergencyAlarmSettingsResponse.emergencyAlarmSettings.length > 0;
    const hasOrganisationId = currentSelectedEmergencyAlarmSettings?.organisationUnitId === setting.organisationUnitId;
    const isDefaultOrganisationSetting = !setting.organisationUnitId && !currentSelectedEmergencyAlarmSettings?.organisationUnitId;
    const isOpen = currentSelectedEmergencyAlarmSettings && (hasOrganisationId || isDefaultOrganisationSetting);

    showDefaultConfirmationPopUp({
      action: () => {
        if (!hasOrganisationSetting && viewingMode === 'none') {
          setViewingMode('editing');
          setCurrentSelectedEmergencyAlarmSettings(setting);
        } else if (isOpen) {
          setViewingMode('none');
          setCurrentSelectedEmergencyAlarmSettings(undefined);
          form.reset();
        } else {
          setViewingMode(hasOrganisationSetting ? 'viewing' : 'none');
          setCurrentSelectedEmergencyAlarmSettings(hasOrganisationSetting ? setting : undefined);
        }
      },
      shouldShow: form.formState.isDirty,
    });
  };

  const resetSettingDetailsForm = (setting: EmergencyAlarmSetting): void => {
    form.reset({
      ...setting,
      divergentSettingEnabled: !setting.isDefaultTemplateSetting,
    });
  };

  const submitEmergencyAlarmSetting = (fieldValues: EmergencyAlarmSettingsFormValues): void => {
    const { organisationUnitId, id } = currentSelectedEmergencyAlarmSettings || {};

    if (!fieldValues.divergentSettingEnabled) {
      if (organisationUnitId) {
        deleteEmergencyAlarmSetting(organisationUnitId);
      }
      return;
    }

    const setting: EmergencyAlarmSetting = {
      id: id ?? '',
      organisationUnitId: organisationUnitId ?? null,
      emergencyAlarmEnabled: fieldValues.emergencyAlarmEnabled,
      activationMethod: fieldValues.activationMethod,
      deviceTriggerAction: fieldValues.deviceTriggerAction,
      preAlarmEnabled: fieldValues.preAlarmEnabled,
      preAlarmDurationS: fieldValues.preAlarmDurationS,
      soundEnabled: fieldValues.soundEnabled,
      locationType: fieldValues.locationType,
      isDefaultTemplateSetting: true,
    };

    if (!setting.organisationUnitId && !setting.emergencyAlarmEnabled && form.formState.isDirty) {
      showConfirmationPopUp(
        () => upsertEmergencyAlarmSetting(setting),
        t('details.disableEmergencyAlarmPopup.title'),
        t('details.disableEmergencyAlarmPopup.text'),
        t('details.disableEmergencyAlarmPopup.confirm'),
        t('details.disableEmergencyAlarmPopup.cancel'),
      );
    } else {
      upsertEmergencyAlarmSetting(setting);
    }
  };

  const switchToEditingMode = (): void => setViewingMode('editing');

  const cancelEditing = (): void => {
    setViewingMode('viewing');
    form.reset({
      ...currentSelectedEmergencyAlarmSettings,
      divergentSettingEnabled: !currentSelectedEmergencyAlarmSettings?.isDefaultTemplateSetting,
    });
  };

  useEffect(() => {
    if (upsertEmergencyAlarmSettingIsSuccess && upsertEmergencyAlarmSettingData) {
      updateCurrentEmergencyAlarmSetting(upsertEmergencyAlarmSettingData);
      setViewingMode('viewing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertEmergencyAlarmSettingIsSuccess]);

  useEffect(() => {
    enablePopUpGuard(form.formState.isDirty);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.formState.isDirty]);

  useEffect(() => {
    if (deleteEmergencyAlarmSettingData) {
      resetCurrentEmergencyAlarmSettingToDefault();
      setViewingMode('viewing');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [deleteEmergencyAlarmSettingData]);

  useEffect(() => {
    if (upsertEmergencyAlarmSettingIsError || deleteEmergencyAlarmSettingIsError) {
      const error = upsertEmergencyAlarmSettingIsError ? upsertEmergencyAlarmSettingError : deleteEmergencyAlarmSettingError;

      setErrorMessage(error ?? undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [upsertEmergencyAlarmSettingIsError, deleteEmergencyAlarmSettingIsError]);

  useEffect(() => {
    if (currentSelectedEmergencyAlarmSettings) {
      resetSettingDetailsForm(currentSelectedEmergencyAlarmSettings);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentSelectedEmergencyAlarmSettings]);

  useEffect(() => {
    if (watchDivergentSettingEnabled && viewingMode === 'editing') {
      form.reset(
        {
          ...currentSelectedEmergencyAlarmSettings,
          divergentSettingEnabled: true,
        },
        { keepDirty: true },
      );
    } else if (!watchDivergentSettingEnabled && viewingMode === 'editing') {
      form.reset(
        {
          ...emergencyAlarmSettingsResponse?.defaultTemplateSetting,
          divergentSettingEnabled: false,
        },
        { keepDirty: true },
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchDivergentSettingEnabled]);

  return {
    currentSelectedEmergencyAlarmSettings,
    viewingMode,
    form,
    upsertIsLoading: upsertEmergencyAlarmSettingIsLoading,
    deleteIsLoading: deleteEmergencyAlarmSettingIsLoading,
    cancelEditing,
    switchToEditingMode,
    selectSettingDetails,
    submitEmergencyAlarmSetting,
  };
};
export default useEmergencyAlarmSettingsDetails;
